import React, { useRef, useState } from "react";
import { Upload, Loader2, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export const inputCls = "w-full rounded-xl border border-[#dcebe8] bg-white px-3.5 py-2.5 text-[14.5px] outline-none transition-colors focus:border-[#0e8c7f] focus:ring-2 focus:ring-[#0e8c7f]/15";

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[13px] font-semibold text-[#3d504d]">{label}</span>
    {children}
  </label>
);

export const ImageField = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#e3f0ed] bg-[#f7fbfa]">
        {value ? <img src={imgUrl(value)} alt="" className="h-full w-full object-contain" /> : <Upload size={16} className="text-[#9aaba8]" />}
      </div>
      <input className={inputCls} placeholder="Image URL or upload" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <button type="button" onClick={() => ref.current?.click()} className="flex shrink-0 items-center gap-1.5 rounded-xl border border-[#0e8c7f] px-3 py-2.5 text-[13.5px] font-semibold text-[#0e8c7f] hover:bg-[#effaf7]">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
    </div>
  );
};

export const renderInput = (f, val, set) => {
  if (f.type === "textarea") return <textarea className={`${inputCls} min-h-[90px] resize-y`} value={val ?? ""} onChange={(e) => set(e.target.value)} />;
  if (f.type === "image") return <ImageField value={val} onChange={set} />;
  if (f.type === "select")
    return (
      <Select value={val || f.options[0]} onValueChange={set}>
        <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  return <input className={inputCls} value={val ?? ""} onChange={(e) => set(e.target.value)} />;
};

export const ListEditor = ({ items = [], onChange, fields, newItem, itemLabel = "Item", titleKey }) => {
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const c = [...items];
    [c[i], c[j]] = [c[j], c[i]];
    onChange(c);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-[#e3f0ed] bg-[#fbfdfd] p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[13.5px] font-semibold text-[#0f2e2a]">{itemLabel} {i + 1}{titleKey && it[titleKey] ? ` — ${String(it[titleKey]).slice(0, 40)}` : ""}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="rounded-md p-1.5 text-[#5b6d6a] hover:bg-[#e8f3f0]" aria-label="Move up"><ChevronUp size={16} /></button>
              <button type="button" onClick={() => move(i, 1)} className="rounded-md p-1.5 text-[#5b6d6a] hover:bg-[#e8f3f0]" aria-label="Move down"><ChevronDown size={16} /></button>
              <button type="button" onClick={() => remove(i)} className="rounded-md p-1.5 text-red-500 hover:bg-red-50" aria-label="Remove"><Trash2 size={16} /></button>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "image" || f.full ? "md:col-span-2" : ""}>
                <Field label={f.label}>{renderInput(f, it[f.key], (v) => update(i, f.key, v))}</Field>
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { ...newItem }])} className="flex items-center gap-1.5 rounded-xl border border-dashed border-[#0e8c7f] px-4 py-2.5 text-[14px] font-semibold text-[#0e8c7f] hover:bg-[#effaf7]">
        <Plus size={16} /> Add {itemLabel.toLowerCase()}
      </button>
    </div>
  );
};
